import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { CodeXml, Eye, Monitor, Palette, Save, Smartphone, Sparkles, Trash2, Wand2 } from 'lucide-react';
import { applyTemplate, TEMPLATE_SAMPLE_BODY, type DomainTemplate, type DomainTemplateEntry } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { Favicon } from './Avatar';
import { HtmlSourceEditor } from './RichEditor';
import { confirmDialog } from './Dialogs';
import { Button, Chip, cn, Input, Modal, Textarea, Toggle } from './ui';

export const templatesKey = ['templates'] as const;

export function useTemplates() {
  return useQuery({ queryKey: templatesKey, queryFn: api.templates, staleTime: 5 * 60_000 });
}

/** The enabled template for a sending address, if its domain has one. */
export function useTemplateFor(address: string | undefined): DomainTemplate | undefined {
  const entries = useTemplates().data;
  const domain = address?.split('@')[1]?.toLowerCase();
  const t = entries?.find((e) => e.domain === domain)?.template;
  return t?.enabled ? t : undefined;
}

const IDEAS = [
  'Deep navy header with our wordmark, warm gold accents, clean white card, subtle footer with address and website link',
  'Minimal and modern: lots of white space, charcoal text, one bright accent color for links and a thin top bar',
  'Playful gaming vibe: dark slate background, neon purple and cyan accents, rounded card, bold header',
  'Corporate and trustworthy: light gray background, blue header band, serif headings, legal footer line',
];

function Preview({ html, width }: { html: string; width: number }) {
  const doc = useMemo(
    () => applyTemplate(html, { content: TEMPLATE_SAMPLE_BODY, senderName: 'Chuck Giddens', senderEmail: 'chuck@example.com', domain: 'example.com' }),
    [html],
  );
  return (
    <div className="flex h-full items-start justify-center overflow-auto bg-[repeating-conic-gradient(var(--line)_0_25%,transparent_0_50%)] bg-[length:20px_20px] p-4">
      <motion.div layout className="overflow-hidden rounded-xl bg-white shadow-2xl" style={{ width }}>
        <iframe title="Template preview" sandbox="allow-same-origin" srcDoc={doc} className="block h-[640px] w-full border-0" />
      </motion.div>
    </div>
  );
}

/** Design a branded email template for one of the user's domains with a natural-language brief. */
export function TemplateDesigner({ initialDomain, compact }: { initialDomain?: string; compact?: boolean }) {
  const qc = useQueryClient();
  const me = useMe().data;
  const entries = useTemplates().data ?? [];
  const [domain, setDomain] = useState<string | undefined>(initialDomain);
  const entry: DomainTemplateEntry | undefined = entries.find((e) => e.domain === domain) ?? entries[0];
  const [draft, setDraft] = useState<DomainTemplate | null>(null);
  const [prompt, setPrompt] = useState('');
  const [company, setCompany] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  const [view, setView] = useState<'preview' | 'html'>('preview');
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (!domain && entries[0]) setDomain(entries[0].domain);
  }, [entries, domain]);

  useEffect(() => {
    const t = entry?.template ?? null;
    setDraft(t);
    setPrompt(t?.prompt ?? '');
    setLogoUrl(t?.logoUrl ?? '');
    setCompany(t?.name && !t.name.endsWith(' brand') ? '' : '');
    setNotes(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.domain, entry?.template?.updatedUtc]);

  if (!entry) return <div className="p-6 text-[13px] text-muted">You have no addresses on a hosted domain yet.</div>;
  const canEdit = entry.canEdit;
  const dirty = JSON.stringify(draft) !== JSON.stringify(entry.template ?? null);

  const generate = async (refine: boolean) => {
    if (!prompt.trim()) {
      toast.error('Describe the look you want first');
      return;
    }
    setBusy(true);
    try {
      const d = await api.designTemplate({
        domain: entry.domain,
        prompt: prompt.trim(),
        logoUrl: logoUrl.trim() || undefined,
        companyName: company.trim() || undefined,
        currentHtml: refine ? draft?.html : undefined,
      });
      setDraft({
        domain: entry.domain,
        name: d.name,
        html: d.html,
        enabled: draft?.enabled ?? true,
        prompt: prompt.trim(),
        logoUrl: logoUrl.trim() || undefined,
        primaryColor: d.primaryColor,
        accentColor: d.accentColor,
        backgroundColor: d.backgroundColor,
        fontFamily: d.fontFamily,
      });
      setNotes(d.notes);
      setView('preview');
    } catch (e) {
      toast.error(`Design failed: ${errorMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.saveTemplate(draft);
      await qc.invalidateQueries({ queryKey: templatesKey });
      toast.success(`Template saved for everyone @${entry.domain}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!(await confirmDialog(`Remove the ${entry.domain} template?`, 'Messages from this domain will be sent without branding.', { confirmLabel: 'Remove', danger: true }))) return;
    await api.deleteTemplate(entry.domain);
    await qc.invalidateQueries({ queryKey: templatesKey });
    setDraft(null);
  };

  return (
    <div className={cn('grid gap-5', compact ? 'grid-cols-[340px_1fr]' : 'grid-cols-[360px_1fr]')}>
      {/* brief */}
      <div className="space-y-4">
        {entries.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {entries.map((e) => (
              <Chip key={e.domain} active={e.domain === entry.domain} onClick={() => setDomain(e.domain)}>
                <Favicon domain={e.domain} size={13} /> {e.domain}
              </Chip>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2.5">
          <Favicon domain={entry.domain} size={22} />
          <div>
            <div className="text-[14px] font-semibold">{entry.domain}</div>
            <div className="text-[11.5px] text-muted">
              {entry.template ? `“${entry.template.name}” · shared with every ${entry.domain} mailbox` : 'No template yet'}
            </div>
          </div>
        </div>

        <label className="block text-[12px] font-medium text-muted">
          Describe the look &amp; feel
          <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Colors, mood, header, logo placement, footer details, social links…" className="mt-1" />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {IDEAS.map((i) => (
            <button key={i} onClick={() => setPrompt(i)} className="rounded-full border border-line px-2.5 py-1 text-left text-[11px] text-muted transition hover:border-accent/40 hover:text-fg">
              {i.split(':')[0].split(',')[0]}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[12px] text-muted">Brand name<Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Dynamic Tabletop Systems" /></label>
          <label className="text-[12px] text-muted">Logo image URL<Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…/logo.png" /></label>
        </div>
        <div className="flex gap-2">
          <Button variant="ai" loading={busy && !draft} icon={<Sparkles className="size-4" />} onClick={() => void generate(false)} disabled={busy} className="flex-1">
            {draft ? 'Design new' : 'Design with AI'}
          </Button>
          {draft && (
            <Button variant="soft" loading={busy} icon={<Wand2 className="size-4" />} onClick={() => void generate(true)} disabled={busy}>
              Refine
            </Button>
          )}
        </div>
        {notes && <p className="rounded-xl bg-accent/10 px-3 py-2 text-[12px] text-fg/80"><Sparkles className="mr-1 inline size-3 text-accent" />{notes}</p>}

        {draft && (
          <div className="space-y-3 rounded-2xl border border-line p-4">
            <label className="block text-[12px] text-muted">Template name<Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <div className="flex items-center gap-2">
              <Palette className="size-4 text-muted" />
              {[draft.primaryColor, draft.accentColor, draft.backgroundColor].filter(Boolean).map((c) => (
                <span key={c} title={c} className="size-6 rounded-md border border-line-strong" style={{ background: c }} />
              ))}
              <span className="truncate text-[11.5px] text-faint">{draft.fontFamily}</span>
            </div>
            <Toggle label="Use for new messages" hint="Each sender can still switch it off per message." checked={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} />
            {canEdit ? (
              <div className="flex gap-2">
                <Button variant="primary" loading={saving} disabled={!dirty} icon={<Save className="size-4" />} onClick={save} className="flex-1">
                  Save for @{entry.domain}
                </Button>
                {entry.template && <Button variant="ghost" icon={<Trash2 className="size-4" />} onClick={remove}>Remove</Button>}
              </div>
            ) : (
              <p className="text-[12px] text-muted">Only an administrator of this domain can save its template.</p>
            )}
          </div>
        )}
        {me && !canEdit && !draft && <p className="text-[12px] text-muted">Ask an administrator of {entry.domain} to set up its branding.</p>}
      </div>

      {/* preview / source */}
      <div className={cn('flex min-h-[560px] flex-col overflow-hidden rounded-2xl border border-line bg-bg/40', busy && 'ai-ring busy')}>
        <div className="flex items-center gap-1 border-b border-line px-3 py-2">
          <div className="flex rounded-lg bg-line p-0.5">
            <button onClick={() => setView('preview')} className={cn('flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium', view === 'preview' ? 'bg-raised shadow-sm' : 'text-muted')}><Eye className="size-3.5" />Preview</button>
            <button onClick={() => setView('html')} disabled={!draft} className={cn('flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium disabled:opacity-40', view === 'html' ? 'bg-raised shadow-sm' : 'text-muted')}><CodeXml className="size-3.5" />HTML</button>
          </div>
          <span className="flex-1" />
          {view === 'preview' && (
            <div className="flex rounded-lg bg-line p-0.5">
              <button onClick={() => setDevice('desktop')} title="Desktop" className={cn('flex size-6 items-center justify-center rounded-md', device === 'desktop' ? 'bg-raised shadow-sm' : 'text-muted')}><Monitor className="size-3.5" /></button>
              <button onClick={() => setDevice('mobile')} title="Mobile" className={cn('flex size-6 items-center justify-center rounded-md', device === 'mobile' ? 'bg-raised shadow-sm' : 'text-muted')}><Smartphone className="size-3.5" /></button>
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1">
          {busy && !draft ? (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <Sparkles className="size-8 animate-pulse text-accent" />
              <div className="shimmer-text text-[15px] font-medium">AI is designing your template…</div>
              <div className="text-[12px] text-faint">Email-safe tables, inline styles, Outlook-friendly</div>
            </div>
          ) : !draft ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
              <Palette className="size-8 text-faint" />
              <div className="text-[14px] font-semibold">Describe your brand, AI builds the template</div>
              <div className="max-w-sm text-[12.5px] text-muted">Every message sent from @{entry.domain} gets the same header, colors and footer — on desktop and iOS.</div>
            </div>
          ) : view === 'html' ? (
            <div className="flex h-full flex-col">
              <HtmlSourceEditor html={draft.html} onChange={(html) => setDraft({ ...draft, html })} minHeight={560} />
              {!draft.html.includes('{{content}}') && <div className="bg-danger/10 px-3 py-1.5 text-[12px] text-danger">The template must contain {'{{content}}'} where the message goes.</div>}
            </div>
          ) : (
            <Preview html={draft.html} width={device === 'desktop' ? 640 : 375} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Modal wrapper used from the composer. */
export function TemplateDesignerModal({ open, onClose, domain }: { open: boolean; onClose: () => void; domain?: string }) {
  return (
    <Modal open={open} onClose={onClose} width={1180} title={<span className="flex items-center gap-2"><Palette className="size-4 text-accent" />Brand template</span>}>
      <div className="p-5">{open && <TemplateDesigner initialDomain={domain} compact />}</div>
    </Modal>
  );
}
